import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Ticket, Plus, Pencil, Trash2, Power, ListChecks, Users, X } from "lucide-react";
import { api, idr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import InputRupiah from "@/components/InputRupiah";
import { DatePicker } from "@/components/ui/date-picker";
import { formatTanggal, hariIniISO } from "@/lib/tanggal";
import { useCabangOptions } from "@/lib/cabang";
import {
  PageHeader, TidakPunyaAkses, SearchInput, SortTh, useSortState, sortRows, usePaginasi,
  Paginasi, unduhCsv, TombolEksporCsv, Modal, Field, inputCls, selectCls, textareaCls,
  TIPE_DISKON, useCampaignMap, namaCampaign, adaField, kolomMarginAda,
  FilterSelect, FilterRentangTanggal, TombolResetFilter, useOpsiFilter,
  adaFilterAktif, paramFilter,
} from "./_shared";

const emptyForm = () => ({
  code: "", nama: "", campaign_id: "", tipe_diskon: "persen", nilai_diskon: 0, min_transaksi: 0,
  tanggal_mulai: hariIniISO(), tanggal_selesai: "", usage_limit: "", per_customer_limit: "", cabang: [],
  // Kosong = semua sales boleh memakai (perilaku lama). Lihat PilihSales di bawah.
  sales_ids: [], catatan: "",
});

/** Opsi sales untuk pembatasan pemakai promo.
 *
 *  Memakai GET /employees yang memang sengaja terbuka untuk semua akun dengan
 *  kolom ringkas (lihat docstring `list_employees` di backend) — dropdown
 *  "pilih karyawan" di seluruh aplikasi memakai endpoint yang sama, jadi
 *  pengelola promo tidak perlu izin modul Data Karyawan hanya untuk memilih
 *  nama. Karyawan nonaktif disaring: mereka tidak akan pernah jadi penjual di
 *  nota baru, jadi memunculkannya cuma memperpanjang daftar.
 */
function useSalesOptions(aktif) {
  const { data } = useDataQuery(
    ["employees", "opsi-sales-promo"],
    () => api.get("/employees").then((r) => r.data),
    { enabled: aktif },
  );
  return useMemo(
    () => (data || []).filter((e) => e.is_active !== false).map((e) => ({
      id: e.id, nama: e.nama || "", branch: e.branch || "",
    })),
    [data],
  );
}

/** Pemilih "sales yang boleh memakai kode ini".
 *
 *  Daftar dikirim ke backend sebagai employees.id, BUKAN nama — sales yang
 *  menikah atau namanya dikoreksi di master karyawan tidak boleh memutus daftar
 *  promo yang sudah berjalan. `namaSnapshot` (dari `promo.sales_nama`) hanya
 *  dipakai menampilkan nama karyawan yang sudah tidak ada lagi di master,
 *  supaya barisnya tidak berubah jadi id mentah yang tidak bisa dibaca siapa pun.
 */
function PilihSales({ terpilih, namaSnapshot, opsi, onChange }) {
  const [cari, setCari] = useState("");
  const petaNama = useMemo(() => Object.fromEntries(opsi.map((o) => [o.id, o.nama])), [opsi]);
  const cocok = useMemo(() => {
    const t = cari.trim().toLowerCase();
    const rows = t ? opsi.filter((o) => o.nama.toLowerCase().includes(t) || o.branch.toLowerCase().includes(t)) : opsi;
    return rows.slice(0, 60);
  }, [opsi, cari]);

  const toggle = (id) => onChange(terpilih.includes(id) ? terpilih.filter((x) => x !== id) : [...terpilih, id]);

  return (
    <div className="space-y-2" data-testid="mkt-promo-f-sales">
      {terpilih.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {terpilih.map((id, i) => (
            <span key={id} className="text-xs px-2 py-1 rounded-full bg-emerald-600 text-white inline-flex items-center gap-1">
              {petaNama[id] || namaSnapshot?.[i] || "(karyawan sudah dihapus)"}
              <button type="button" onClick={() => toggle(id)} data-testid={`mkt-promo-f-sales-buang-${id}`}
                      className="hover:bg-emerald-700 rounded-full" aria-label="Hapus dari daftar">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <input value={cari} onChange={(e) => setCari(e.target.value)} data-testid="mkt-promo-f-sales-cari"
             className={inputCls} placeholder="Cari nama sales / cabang..." />
      <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
        {cocok.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">Tidak ada sales yang cocok.</p>}
        {cocok.map((o) => (
          <label key={o.id} data-testid={`mkt-promo-f-sales-${o.id}`}
                 className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={terpilih.includes(o.id)} onChange={() => toggle(o.id)} className="accent-emerald-600" />
            <span className="text-slate-700">{o.nama}</span>
            {o.branch && <span className="text-[11px] text-slate-400">{o.branch}</span>}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function PromoCodes() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_promo", "view");
  const bolehTambah = can("marketing_promo", "create");
  const bolehEdit = can("marketing_promo", "update");
  const bolehHapus = can("marketing_promo", "delete");
  const bolehCampaign = can("marketing_campaign", "view");
  const bolehMargin = can("marketing_margin", "view");
  const { campaignMap, campaignList } = useCampaignMap(bolehCampaign);
  const cabangOpsi = useCabangOptions();
  // Hanya diambil kalau akun ini memang bisa membuat/menyunting promo — tidak
  // ada gunanya menarik daftar karyawan untuk layar yang cuma bisa melihat.
  const salesOpsi = useSalesOptions(bolehTambah || bolehEdit);

  const [tab, setTab] = useState("promo");

  // ---- Kode promo ----
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const [aktifFilter, setAktifFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [cabangFilter, setCabangFilter] = useState("");
  const [tipeFilter, setTipeFilter] = useState("");
  const [pembuatFilter, setPembuatFilter] = useState("");
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const { sortKey, sortDir, toggle } = useSortState("created_at", "desc");
  const [form, setForm] = useState(null);
  const [editCode, setEditCode] = useState(null); // {id, code} — code ditampilkan read-only saat edit
  const [salesNamaSnapshot, setSalesNamaSnapshot] = useState([]);
  const [busy, setBusy] = useState(false);
  const opsiFilter = useOpsiFilter("/marketing/promo/opsi-filter", bolehLihat);

  // Pencarian & filter DI SERVER: daftar kode promo dipotong 2000 baris di
  // backend, jadi menyaringnya di layar membuat kode ke-2001 tidak pernah
  // ketemu — dan layarnya melapor "tidak ada hasil" (CLAUDE.md).
  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "promo", aktifFilter, campaignFilter, cabangFilter, tipeFilter,
      pembuatFilter, qDebounced, dari, sampai],
    () => api.get("/marketing/promo", {
      params: paramFilter({
        is_active: aktifFilter ? aktifFilter === "aktif" : "",
        campaign_id: campaignFilter, cabang: cabangFilter, tipe_diskon: tipeFilter,
        dibuat_oleh: pembuatFilter, q: qDebounced.trim(), dari, sampai,
      }),
    }).then((r) => r.data),
    { enabled: bolehLihat },
  );
  const rowsPromo = useMemo(() => data || [], [data]);
  const sorted = sortRows(rowsPromo, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(sorted.length, 15);
  const halaman = sorted.slice(awal, akhir);

  const filterAktif = adaFilterAktif(q, aktifFilter, campaignFilter, cabangFilter,
                                     tipeFilter, pembuatFilter, dari, sampai);
  const resetFilter = () => {
    setQ(""); setAktifFilter(""); setCampaignFilter(""); setCabangFilter("");
    setTipeFilter(""); setPembuatFilter(""); setDari(""); setSampai("");
  };

  const eksporCsv = () => {
    unduhCsv(`kode-promo-${hariIniISO()}.csv`, [
      { label: "Kode", value: (r) => r.code },
      { label: "Nama", value: (r) => r.nama },
      { label: "Campaign", value: (r) => namaCampaign(campaignMap, r.campaign_id) },
      { label: "Tipe Diskon", value: (r) => (r.tipe_diskon === "persen" ? "Persen" : "Nominal") },
      { label: "Nilai Diskon", value: (r) => r.nilai_diskon },
      { label: "Min Transaksi", value: (r) => r.min_transaksi },
      { label: "Periode", value: (r) => `${r.tanggal_mulai} s/d ${r.tanggal_selesai}` },
      { label: "Pemakaian", value: (r) => `${r.used_count}${r.usage_limit ? `/${r.usage_limit}` : ""}` },
      { label: "Sales Khusus", value: (r) => ((r.sales_ids || []).length ? (r.sales_nama || []).join(" | ") : "Semua sales") },
      { label: "Status", value: (r) => (r.is_active ? "Aktif" : "Nonaktif") },
    ], sorted);
  };

  const bukaTambah = () => { setEditCode(null); setSalesNamaSnapshot([]); setForm(emptyForm()); };
  const bukaEdit = (p) => {
    setEditCode({ id: p.id, code: p.code, tipe_diskon: p.tipe_diskon });
    setForm({
      code: p.code, nama: p.nama, campaign_id: p.campaign_id || "", tipe_diskon: p.tipe_diskon,
      nilai_diskon: p.nilai_diskon, min_transaksi: p.min_transaksi || 0,
      tanggal_mulai: p.tanggal_mulai, tanggal_selesai: p.tanggal_selesai,
      usage_limit: p.usage_limit ?? "", per_customer_limit: p.per_customer_limit ?? "",
      cabang: p.cabang || [], sales_ids: p.sales_ids || [], catatan: p.catatan || "",
    });
    // Snapshot nama dari server dibawa terpisah dari form: dipakai HANYA untuk
    // menampilkan sales yang karyawannya sudah dihapus dari master.
    setSalesNamaSnapshot(p.sales_nama || []);
  };

  const simpan = async () => {
    if (!form.nama.trim()) { toast.error("Nama promo wajib diisi"); return; }
    if (!form.nilai_diskon || form.nilai_diskon <= 0) { toast.error("Nilai diskon wajib lebih dari 0"); return; }
    setBusy(true);
    try {
      if (editCode) {
        await api.patch(`/marketing/promo/${editCode.id}`, {
          nama: form.nama, nilai_diskon: form.nilai_diskon, min_transaksi: form.min_transaksi,
          tanggal_mulai: form.tanggal_mulai, tanggal_selesai: form.tanggal_selesai,
          usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
          per_customer_limit: form.per_customer_limit === "" ? null : Number(form.per_customer_limit),
          // Dikirim SELALU (termasuk saat kosong) supaya pembatasan bisa dicabut
          // lagi — backend memperlakukan [] sebagai "semua sales boleh".
          cabang: form.cabang, sales_ids: form.sales_ids, catatan: form.catatan,
        });
        toast.success("Kode promo diperbarui");
      } else {
        if (!form.code.trim()) { toast.error("Kode promo wajib diisi"); setBusy(false); return; }
        await api.post("/marketing/promo", {
          ...form, campaign_id: form.campaign_id || null,
          usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
          per_customer_limit: form.per_customer_limit === "" ? null : Number(form.per_customer_limit),
        });
        toast.success("Kode promo dibuat");
      }
      setForm(null); setEditCode(null); refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan kode promo"); }
    finally { setBusy(false); }
  };

  const toggleAktif = async (p) => {
    try {
      await api.patch(`/marketing/promo/${p.id}`, { is_active: !p.is_active });
      toast.success(`Kode promo "${p.code}" ${p.is_active ? "dinonaktifkan" : "diaktifkan"}`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status kode promo"); }
  };

  const hapus = async (p) => {
    if (!window.confirm(`Hapus kode promo "${p.code}"? Kode yang sudah pernah dipakai tidak bisa dihapus.`)) return;
    try {
      const r = await api.delete(`/marketing/promo/${p.id}`);
      toast.success(r.data.message || "Kode promo dihapus");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus kode promo"); }
  };

  const toggleCabang = (c) => {
    setForm((f) => ({ ...f, cabang: f.cabang.includes(c) ? f.cabang.filter((x) => x !== c) : [...f.cabang, c] }));
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Ticket} title="Kode Promo" />
        <TidakPunyaAkses label="Kode Promo" testid="mkt-promo-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-promo">
      <PageHeader
        icon={Ticket} title="Kode Promo"
        desc="Kelola kode promo dan pantau riwayat pemakaiannya di transaksi POS."
        actions={tab === "promo" && <>
          <TombolEksporCsv onClick={eksporCsv} testid="mkt-promo-export" />
          {bolehTambah && (
            <button onClick={bukaTambah} data-testid="mkt-promo-tambah"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Buat Kode Promo
            </button>
          )}
        </>}
      />

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1 w-fit">
        <button onClick={() => setTab("promo")} data-testid="mkt-promo-tab-kode"
                className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "promo" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <Ticket className="w-4 h-4" /> Kode Promo
        </button>
        <button onClick={() => setTab("redemptions")} data-testid="mkt-promo-tab-riwayat"
                className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "redemptions" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <ListChecks className="w-4 h-4" /> Riwayat Pemakaian
        </button>
      </div>

      {tab === "promo" ? (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <SearchInput value={q} onChange={setQ} placeholder="Cari kode/nama/catatan promo..." testid="mkt-promo-cari" className="w-64" />
            <select value={aktifFilter} onChange={(e) => setAktifFilter(e.target.value)} data-testid="mkt-promo-filter-aktif" className={`${selectCls} h-9 w-auto`}>
              <option value="">Semua Status</option>
              <option value="aktif">Aktif</option>
              <option value="nonaktif">Nonaktif</option>
            </select>
            {bolehCampaign && (
              <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="mkt-promo-filter-campaign" className={`${selectCls} h-9 w-auto`}>
                <option value="">Semua Campaign</option>
                {campaignList.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
              </select>
            )}
            {/* Cabang: promo lintas-cabang (daftar cabangnya kosong) IKUT muncul —
                backend yang mengurusnya, lihat `_query_daftar_promo`. */}
            <FilterSelect value={cabangFilter} onChange={setCabangFilter} testid="mkt-promo-filter-cabang"
                          semuaLabel="Semua Cabang" opsi={cabangOpsi} />
            <FilterSelect value={tipeFilter} onChange={setTipeFilter} testid="mkt-promo-filter-tipe"
                          semuaLabel="Semua Tipe Diskon"
                          opsi={TIPE_DISKON.map((t) => ({ value: t, label: t === "persen" ? "Persen (%)" : "Nominal (Rp)" }))} />
            <FilterSelect value={pembuatFilter} onChange={setPembuatFilter} testid="mkt-promo-filter-pembuat"
                          semuaLabel="Semua User" opsi={opsiFilter.dibuat_oleh} />
            <FilterRentangTanggal dari={dari} sampai={sampai} onDari={setDari} onSampai={setSampai}
                                  testid="mkt-promo-filter-periode"
                                  labelDari="Berlaku dari" labelSampai="Berlaku s/d" />
            <TombolResetFilter aktif={filterAktif} onReset={resetFilter} testid="mkt-promo-reset-filter" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <SortTh label="Kode / Nama" sortKeyName="code" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-promo-sort-kode" />
                    <th className="px-3 py-2.5 font-semibold">Campaign</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Diskon</th>
                    <th className="px-3 py-2.5 font-semibold">Periode</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Pemakaian</th>
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && halaman.length === 0}
                                varian="tabel" kolom={7} pesanKosong="Belum ada kode promo." data-testid="mkt-promo-state">
                    {halaman.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50/60" data-testid={`mkt-promo-row-${p.id}`}>
                        <td className="px-3 py-2.5">
                          <p className="font-mono font-semibold text-slate-900">{p.code}</p>
                          <p className="text-xs text-slate-500">{p.nama}</p>
                          {/* Pembatasan sales harus kelihatan tanpa membuka form —
                              kalau tidak, "kenapa kode ini ditolak di kasir?"
                              berakhir jadi tiket dukungan tiap kali. */}
                          {(p.sales_ids || []).length > 0 && (
                            <span data-testid={`mkt-promo-sales-badge-${p.id}`}
                                  title={(p.sales_nama || []).join(", ")}
                                  className="mt-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                              <Users className="w-3 h-3" /> {p.sales_ids.length} sales
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs">{namaCampaign(campaignMap, p.campaign_id)}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{p.tipe_diskon === "persen" ? `${p.nilai_diskon}%` : idr(p.nilai_diskon)}</td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs">{formatTanggal(p.tanggal_mulai)} – {formatTanggal(p.tanggal_selesai)}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{p.used_count}{p.usage_limit ? `/${p.usage_limit}` : ""}</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${p.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                            {p.is_active ? "Aktif" : "Nonaktif"}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            {bolehEdit && (
                              <button onClick={() => toggleAktif(p)} data-testid={`mkt-promo-toggle-${p.id}`} title={p.is_active ? "Nonaktifkan" : "Aktifkan"}
                                      className="w-8 h-8 rounded hover:bg-slate-100 text-slate-500 grid place-items-center"><Power className="w-4 h-4" /></button>
                            )}
                            {bolehEdit && (
                              <button onClick={() => bukaEdit(p)} data-testid={`mkt-promo-edit-${p.id}`}
                                      className="w-8 h-8 rounded hover:bg-slate-100 text-slate-500 grid place-items-center"><Pencil className="w-4 h-4" /></button>
                            )}
                            {bolehHapus && (
                              <button onClick={() => hapus(p)} data-testid={`mkt-promo-hapus-${p.id}`}
                                      className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </LoadingState>
                </tbody>
              </table>
            </div>
            <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-promo-paginasi" />
          </div>
        </>
      ) : (
        <RiwayatPemakaian campaignMap={campaignMap} bolehCampaign={bolehCampaign} campaignList={campaignList}
                          bolehMargin={bolehMargin} opsiFilter={opsiFilter} />
      )}

      {/* Form kode promo */}
      <Modal open={!!form} onClose={() => { setForm(null); setEditCode(null); }} title={editCode ? "Edit Kode Promo" : "Buat Kode Promo"} maxWidth="max-w-xl" testid="mkt-promo-modal"
             footer={<>
               <button onClick={() => { setForm(null); setEditCode(null); }} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpan} disabled={busy} data-testid="mkt-promo-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? "Menyimpan..." : "Simpan"}</button>
             </>}>
        {form && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kode" required hint={editCode ? "Kode tidak bisa diubah setelah dibuat" : undefined}>
                <input value={form.code} disabled={!!editCode} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                       data-testid="mkt-promo-f-code" className={`${inputCls} font-mono uppercase`} placeholder="cth. RAMADAN26" />
              </Field>
              <Field label="Nama" required>
                <input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))} data-testid="mkt-promo-f-nama" className={inputCls} />
              </Field>
            </div>
            {bolehCampaign && (
              <Field label="Campaign (opsional)" hint={editCode ? "Tidak bisa diubah setelah dibuat" : undefined}>
                <select value={form.campaign_id} disabled={!!editCode} onChange={(e) => setForm((f) => ({ ...f, campaign_id: e.target.value }))} data-testid="mkt-promo-f-campaign" className={selectCls}>
                  <option value="">— tidak terkait campaign —</option>
                  {campaignList.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
                </select>
              </Field>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipe Diskon" required hint={editCode ? "Tidak bisa diubah setelah dibuat" : undefined}>
                <select value={form.tipe_diskon} disabled={!!editCode} onChange={(e) => setForm((f) => ({ ...f, tipe_diskon: e.target.value }))} data-testid="mkt-promo-f-tipe" className={selectCls}>
                  {TIPE_DISKON.map((t) => <option key={t} value={t}>{t === "persen" ? "Persen (%)" : "Nominal (Rp)"}</option>)}
                </select>
              </Field>
              <Field label={form.tipe_diskon === "persen" ? "Nilai Diskon (%)" : "Nilai Diskon (Rp)"} required>
                {form.tipe_diskon === "persen" ? (
                  <input type="number" min="0" max="100" value={form.nilai_diskon} onChange={(e) => setForm((f) => ({ ...f, nilai_diskon: Number(e.target.value || 0) }))} data-testid="mkt-promo-f-nilai" className={inputCls} />
                ) : (
                  <InputRupiah value={form.nilai_diskon} onChange={(v) => setForm((f) => ({ ...f, nilai_diskon: v }))} testid="mkt-promo-f-nilai" className={inputCls} />
                )}
              </Field>
            </div>
            <Field label="Minimal Transaksi (Rp)">
              <InputRupiah value={form.min_transaksi} onChange={(v) => setForm((f) => ({ ...f, min_transaksi: v }))} testid="mkt-promo-f-min" className={inputCls} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tanggal Mulai" required>
                <DatePicker value={form.tanggal_mulai} onChange={(v) => setForm((f) => ({ ...f, tanggal_mulai: v }))} data-testid="mkt-promo-f-mulai" />
              </Field>
              <Field label="Tanggal Selesai" required>
                <DatePicker value={form.tanggal_selesai} onChange={(v) => setForm((f) => ({ ...f, tanggal_selesai: v }))} min={form.tanggal_mulai} data-testid="mkt-promo-f-selesai" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kuota Total" hint="Kosongkan untuk tanpa batas">
                <input type="number" min="1" value={form.usage_limit} onChange={(e) => setForm((f) => ({ ...f, usage_limit: e.target.value }))} data-testid="mkt-promo-f-usage" className={inputCls} />
              </Field>
              <Field label="Batas per Pelanggan" hint="Kosongkan untuk tanpa batas">
                <input type="number" min="1" value={form.per_customer_limit} onChange={(e) => setForm((f) => ({ ...f, per_customer_limit: e.target.value }))} data-testid="mkt-promo-f-per-customer" className={inputCls} />
              </Field>
            </div>
            <Field label="Berlaku di Cabang" hint="Kosongkan semua = berlaku di seluruh cabang">
              <div className="flex flex-wrap gap-1.5">
                {cabangOpsi.map((c) => (
                  <button key={c} type="button" onClick={() => toggleCabang(c)} data-testid={`mkt-promo-f-cabang-${c}`}
                          className={`text-xs px-2.5 py-1 rounded-full border ${form.cabang.includes(c) ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Sales yang Boleh Memakai"
                   hint="Kosongkan = semua sales boleh. Yang dicek adalah penjual yang tercatat di nota, bukan akun yang menginput.">
              <PilihSales terpilih={form.sales_ids} namaSnapshot={salesNamaSnapshot} opsi={salesOpsi}
                          onChange={(v) => setForm((f) => ({ ...f, sales_ids: v }))} />
            </Field>
            <Field label="Catatan">
              <textarea value={form.catatan} rows={2} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} data-testid="mkt-promo-f-catatan" className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>
    </div>
  );
}

function RiwayatPemakaian({ campaignMap, bolehCampaign, campaignList, bolehMargin, opsiFilter }) {
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [cabangFilter, setCabangFilter] = useState("");
  const [penjualFilter, setPenjualFilter] = useState("");
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const { sortKey, sortDir, toggle } = useSortState("created_at", "desc");

  // Riwayat dipotong 5000 baris di backend — pencarian & filter WAJIB di server,
  // kalau tidak penebusan ke-5001 tidak akan pernah ketemu (CLAUDE.md).
  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "promo-redemptions", campaignFilter, cabangFilter, penjualFilter, qDebounced, dari, sampai],
    () => api.get("/marketing/promo/redemptions", {
      params: paramFilter({
        campaign_id: campaignFilter, cabang: cabangFilter, penjual: penjualFilter,
        q: qDebounced.trim(), dari, sampai,
      }),
    }).then((r) => r.data),
  );
  const rows = useMemo(() => data || [], [data]);
  const sorted = sortRows(rows, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(sorted.length, 15);
  const halaman = sorted.slice(awal, akhir);
  const marginKolom = kolomMarginAda(rows, "margin", bolehMargin);

  const filterAktif = adaFilterAktif(q, campaignFilter, cabangFilter, penjualFilter, dari, sampai);
  const resetFilter = () => {
    setQ(""); setCampaignFilter(""); setCabangFilter(""); setPenjualFilter(""); setDari(""); setSampai("");
  };

  const eksporCsv = () => {
    const kolom = [
      { label: "Tanggal", value: (r) => r.tanggal },
      { label: "Kode Promo", value: (r) => r.code },
      { label: "Campaign", value: (r) => namaCampaign(campaignMap, r.campaign_id) },
      { label: "Cabang", value: (r) => r.cabang || "" },
      { label: "Pelanggan", value: (r) => r.nama_pelanggan || "" },
      { label: "No HP", value: (r) => r.no_hp || "" },
      { label: "Penjual", value: (r) => r.nama_penjual || "" },
      { label: "Omzet", value: (r) => r.omzet },
    ];
    if (marginKolom) kolom.push({ label: "Margin", value: (r) => (adaField(r, "margin") ? r.margin ?? "" : "") });
    unduhCsv(`riwayat-promo-${hariIniISO()}.csv`, kolom, sorted);
  };

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput value={q} onChange={setQ} placeholder="Cari kode/pelanggan/no HP/penjual..." testid="mkt-promo-red-cari" className="w-64" />
          {bolehCampaign && (
            <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="mkt-promo-red-filter-campaign" className={`${selectCls} h-9 w-auto`}>
              <option value="">Semua Campaign</option>
              {campaignList.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
            </select>
          )}
          <FilterSelect value={cabangFilter} onChange={setCabangFilter} testid="mkt-promo-red-filter-cabang"
                        semuaLabel="Semua Cabang" opsi={opsiFilter?.redemption_cabang} />
          {/* "User" untuk riwayat = sales yang tercatat di nota, bukan akun
              penginput — itu yang dipakai laporan ROI per sales. */}
          <FilterSelect value={penjualFilter} onChange={setPenjualFilter} testid="mkt-promo-red-filter-penjual"
                        semuaLabel="Semua Penjual" opsi={opsiFilter?.penjual} />
          <FilterRentangTanggal dari={dari} sampai={sampai} onDari={setDari} onSampai={setSampai}
                                testid="mkt-promo-red-filter-tanggal" />
          <TombolResetFilter aktif={filterAktif} onReset={resetFilter} testid="mkt-promo-red-reset-filter" />
        </div>
        <TombolEksporCsv onClick={eksporCsv} testid="mkt-promo-red-export" />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-promo-red-sort-tanggal" />
                <th className="px-3 py-2.5 font-semibold">Kode</th>
                <th className="px-3 py-2.5 font-semibold">Pelanggan</th>
                <th className="px-3 py-2.5 font-semibold">Cabang / Penjual</th>
                <SortTh label="Omzet" sortKeyName="omzet" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-promo-red-sort-omzet" />
                {marginKolom && <th className="px-3 py-2.5 font-semibold text-right">Margin</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && halaman.length === 0}
                            varian="tabel" kolom={marginKolom ? 6 : 5} pesanKosong="Belum ada pemakaian kode promo." data-testid="mkt-promo-red-state">
                {halaman.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`mkt-promo-red-row-${r.id}`}>
                    <td className="px-3 py-2.5 text-slate-700">{formatTanggal(r.tanggal)}</td>
                    <td className="px-3 py-2.5 font-mono font-medium text-slate-900">{r.code}</td>
                    <td className="px-3 py-2.5 text-slate-700">{r.nama_pelanggan || "-"}<br /><span className="text-xs text-slate-400">{r.no_hp || "-"}</span></td>
                    <td className="px-3 py-2.5 text-slate-600 text-xs">{r.cabang || "-"}<br />{r.nama_penjual || "-"}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{idr(r.omzet)}</td>
                    {marginKolom && <td className="px-3 py-2.5 text-right font-mono">{adaField(r, "margin") && r.margin != null ? idr(r.margin) : "-"}</td>}
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </div>
        <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-promo-red-paginasi" />
      </div>
    </>
  );
}
